// ... All your imports remain unchanged ...
import React, { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  Polyline,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "../../api/api";
import "../../css/EmployeeLocation.css";

const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

const MapAutoFitBounds = ({ markers }) => {
  const map = useMap();

  useEffect(() => {
    const bounds = L.latLngBounds([]);
    let centralLat = null;
    let centralLng = null;

    markers.forEach((emp) => {
      const points = [];

      // ✅ Combine legacy fields
      [
        { lat: emp.check_in_lat, long: emp.check_in_long },
        { lat: emp.break_in_lat, long: emp.break_in_long },
        { lat: emp.break_out_lat, long: emp.break_out_long },
        { lat: emp.check_out_lat, long: emp.check_out_long },
        { lat: emp.meeting_lat, long: emp.meeting_long },
      ].forEach((p) => {
        if (p.lat && p.long) {
          points.push([parseFloat(p.lat), parseFloat(p.long)]);
        }
      });

      // ✅ Add new `locations`
      if (Array.isArray(emp.locations)) {
        emp.locations.forEach((loc) => {
          if (loc.latitude && loc.longitude) {
            points.push([parseFloat(loc.latitude), parseFloat(loc.longitude)]);
          }
        });
      }

      points.forEach(([lat, long]) => {
        bounds.extend([lat, long]);
      });

      if (!centralLat && points[0]) {
        centralLat = points[0][0];
        centralLng = points[0][1];
      }
    });

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }

    if (centralLat && centralLng) {
      map.setView([centralLat, centralLng], 12);
    }
  }, [markers, map]);

  return null;
};

const EmployeeLocation = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(""); // ← keep it empty initially

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showDatePickers, setShowDatePickers] = useState(false);
  const [employeeLocations, setEmployeeLocations] = useState([]);
  const [locationAddresses, setLocationAddresses] = useState({});
  const [zoomLevel, setZoomLevel] = useState(5);
  const [initialPosition, setInitialPosition] = useState([51.505, -0.09]);
  const [useAerialView, setUseAerialView] = useState(false);

  const [mapData, setMapData] = useState([]); // This is the data shown on map
const [filterSourceData, setFilterSourceData] = useState([]); // This is all data for filtering
const [hasLoadedAllData, setHasLoadedAllData] = useState(false); // ✅ new flag
const [isFilterLoading, setIsFilterLoading] = useState(false);





  // *** Added loading state ***
  const [isLoading, setIsLoading] = useState(true);

useEffect(() => {
  const loadStart = performance.now();

  // ✅ Only fetch today's data for map (initial map data)
  api
    .get("/get/employee/location/today")
    .then((response) => {
      if (response.data.success) {
        const filtered = response.data.data.filter(
          (loc) =>
            loc.locations?.length > 0 ||
            loc.check_in_lat ||
            loc.break_in_lat ||
            loc.break_out_lat ||
            loc.check_out_lat ||
            loc.meeting_lat
        );

        setMapData(filtered); // Show on map
      }
    })
    .catch((err) => console.error("Error fetching today's location data", err))
    .finally(() => {
      // Ensure minimum 5 second load time
      const loadEnd = performance.now();
      const elapsed = loadEnd - loadStart;
      const remaining = 5000 - elapsed;

      if (remaining > 0) {
        setTimeout(() => setIsLoading(false), remaining);
      } else {
        setIsLoading(false);
      }
    });
}, []);



useEffect(() => {
  const isFilterApplied =
    searchTerm.trim() !== "" || selectedDate || (startDate && endDate);

  if (!isFilterApplied) return;

  const fetchAndFilter = async () => {
    setIsFilterLoading(true); // 🔴 Start loading

    if (!hasLoadedAllData) {
      try {
        const response = await api.get("/get/employee/location");
        if (response.data.success) {
          const fullData = response.data.data;
          setFilterSourceData(fullData);
          setHasLoadedAllData(true);
          applyFilters(fullData);
        }
      } catch (err) {
        console.error("Error fetching all location data for filtering", err);
      } finally {
        setIsFilterLoading(false); // ✅ End loading
      }
    } else {
      applyFilters(filterSourceData);
      setIsFilterLoading(false); // ✅ End loading
    }
  };

  const applyFilters = (data) => {
    let dataToFilter = [...data];

    if (searchTerm.trim() !== "") {
      const search = searchTerm.toLowerCase();
      dataToFilter = dataToFilter.filter((emp) => {
        const name = (emp.employee_name || "").toLowerCase();
        const phone = (emp.phone || "").toLowerCase();
        const meetingDate = (
          emp.meeting_date ||
          emp.attendance_date ||
          ""
        ).toLowerCase();
        return (
          name.includes(search) ||
          phone.includes(search) ||
          meetingDate.includes(search)
        );
      });
    }

    if (startDate && endDate) {
      dataToFilter = dataToFilter.filter(
        (emp) =>
          (emp.meeting_date || emp.attendance_date) >= startDate &&
          (emp.meeting_date || emp.attendance_date) <= endDate
      );
    } else if (selectedDate) {
      dataToFilter = dataToFilter.filter(
        (emp) => (emp.meeting_date || emp.attendance_date) === selectedDate
      );
    }

    setMapData(dataToFilter);
  };

  fetchAndFilter();
}, [searchTerm, selectedDate, startDate, endDate]);





  const locationIcon = new L.DivIcon({
    html: '<i class="fa-solid fa-location-dot" style="font-size: 24px; color: red;"></i>',
    iconSize: [30, 30],
    iconAnchor: [15, 30],
  });

  return (
    <section className="employee-section employee-location">
      <div className="inner-content">
        <div className="filter-wrapper">
          <h2>Employee Location</h2>
          <div className="right-filter">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div
              className="date-filter tooltip-wrapper"
              style={{ position: "relative" }}
              data-tooltip="Filter by Date"
            >
              <button onClick={() => setShowDatePickers((prev) => !prev)}>
                <i className="fa-solid fa-calendar"></i>
              </button>
              {showDatePickers && (
                <div
                  className="date-popup"
                  style={{
                    position: "fixed",
                    top: "125px",
                    right: "15px",
                    background: "#fff",
                    padding: "12px 15px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    zIndex: 100,
                    display: "flex",
                    gap: "10px",
                  }}
                >
                  <input
                    type="date"
                    value={startDate}
                    max={getTodayDate()}
                    onChange={(e) => {
                      const val = e.target.value;
                      setStartDate(val);
                      if (val && val > endDate) setEndDate(val);
                      if (val && endDate) setShowDatePickers(false);
                    }}
                  />
                  <input
                    type="date"
                    value={endDate}
                    min={startDate}
                    max={getTodayDate()}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEndDate(val);
                      if (val && val < startDate) setStartDate(val);
                      if (startDate && val) setShowDatePickers(false);
                    }}
                  />
                </div>
              )}
            </div>{" "}
          </div>{" "}
        </div>{" "}
        <div className="map-wrapper">
          {/* Show loader  */}
          {isLoading ? (
            <div
              style={{
                height: "calc(100vh - 130px)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "20px",
                color: "#555",
                fontWeight: "bold",
              }}
            >
              <div class="pin"></div>
              <div class="pulse"></div>
            </div>
          ) : (
            <MapContainer
              center={initialPosition}
              zoom={zoomLevel}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                url={
                  useAerialView
                    ? "https://{s}.satellite.tiles.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}.png?access_token=YOUR_ACCESS_TOKEN"
                    : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                }
              />

              {/* Fit map to markers */}
              <MapAutoFitBounds markers={mapData} />


              {mapData.map((emp) => {
                // *** Fix: define points here to avoid ESLint no-undef ***
                const points = [];

                // legacy fields
                [
                  {
                    lat: emp.check_in_lat,
                    long: emp.check_in_long,
                    label: `Check In at ${emp.check_in_time || "N/A"}`,
                  },
                  {
                    lat: emp.break_in_lat,
                    long: emp.break_in_long,
                    label: `Break In at ${emp.break_in_time || "N/A"}`,
                  },
                  {
                    lat: emp.break_out_lat,
                    long: emp.break_out_long,
                    label: `Break Out at ${emp.break_out_time || "N/A"}`,
                  },
                  {
                    lat: emp.check_out_lat,
                    long: emp.check_out_long,
                    label: `Check Out at ${emp.check_out_time || "N/A"}`,
                  },
                  {
                    lat: emp.meeting_lat,
                    long: emp.meeting_long,
                    label: `Meeting at ${emp.meeting_time || "N/A"}`,
                  },
                ].forEach((p) => {
                  if (p.lat && p.long) points.push(p);
                });

                // new locations with offset
                emp.locations?.forEach((loc, idx) => {
                  if (loc.latitude && loc.longitude) {
                    const baseLat = parseFloat(loc.latitude);
                    const baseLong = parseFloat(loc.longitude);
                    const offset = 0.00005 * idx;

                    points.push({
                      lat: (baseLat + offset).toFixed(7),
                      long: (baseLong + offset).toFixed(7),
                      label: `Meeting with ${loc.dealer_name || "Unknown"} at ${
                        loc.meeting_time || loc.timestamp || "N/A"
                      }`,
                    });
                  }
                });

                return (
                  <React.Fragment key={emp.employee_id || emp.id}>
                    {points.map((point, idx) => (
                      <Marker
                        key={`${emp.employee_id || emp.id}-${idx}`}
                        position={[
                          parseFloat(point.lat),
                          parseFloat(point.long),
                        ]}
                        icon={locationIcon}
                      >
                        <Popup>
                          <div>
                            <p>
                              <strong>
                                {point.label || emp.employee_name}
                              </strong>
                            </p>
                            <p>Employee: {emp.employee_name}</p>
                            <p>Phone: {emp.phone}</p>
                            <p>
                              Address:{" "}
                              {(() => {
                                if (point.label === "Check In")
                                  return (
                                    emp.check_in_address ||
                                    "Address not available"
                                  );
                                if (point.label === "Check Out")
                                  return (
                                    emp.check_out_address ||
                                    "Address not available"
                                  );
                                if (point.label === "Break In")
                                  return (
                                    emp.break_in_address ||
                                    "Address not available"
                                  );
                                if (point.label === "Break Out")
                                  return (
                                    emp.break_out_address ||
                                    "Address not available"
                                  );

                                if (point.label?.startsWith("Meeting with")) {
                                  // try to match label to the location
                                  const matchedLoc = emp.locations?.find(
                                    (loc) =>
                                      point.label.includes(
                                        loc.dealer_name || ""
                                      ) &&
                                      (point.label.includes(loc.meeting_time) ||
                                        point.label.includes(loc.timestamp))
                                  );
                                  return (
                                    matchedLoc?.meeting_address ||
                                    "Address not available"
                                  );
                                }

                                return "Address not available";
                              })()}
                            </p>

                            <p>
                              Date:{" "}
                              {emp.meeting_date || emp.attendance_date || "N/A"}
                            </p>
                          </div>
                        </Popup>
                      </Marker>
                    ))}

                    {/* 🔵 Polyline connecting all points for this employee */}
                    {points.length > 1 && (
                      <Polyline
                        positions={points.map((p) => [
                          parseFloat(p.lat),
                          parseFloat(p.long),
                        ])}
                        color="#2db05f"
                        weight={3}
                        opacity={1}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </MapContainer>
          )}
        </div>
      </div>
    </section>
  );
};

export default EmployeeLocation;
